import { Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { AdminTheme, AdminThemeService } from "../../services/admin-theme.service";
import { AppTheme } from "./app-theme";
import { setBrandingLogoSrc, resolveBrandingLogoUrl } from "./branding-logo-source";
import { resetBrandingLogoDataUriCache } from "../../utils/letter-branding.util";
import {
  applyAdminThemeTokens,
  writeCachedAdminThemeTokens,
  readCachedAdminThemeLogo,
  writeCachedAdminThemeLogo,
  updateFavicon,
} from "./admin-theme-tokens-applier";

/**
 * Loads active admin theme from the backend and applies CSS variables + optional logo URL.
 */
@Injectable({
  providedIn: "root",
})
export class ThemeService {
  private readonly logoUrlSubject = new BehaviorSubject<string>(
    readCachedAdminThemeLogo() ?? AppTheme.logoSrc
  );
  private loadPromise: Promise<void> | null = null;

  constructor(private adminThemeService: AdminThemeService) {}

  readonly logoUrl$ = this.logoUrlSubject.asObservable();

  getLogoUrl(): string {
    return this.logoUrlSubject.value;
  }

  /** Loads active theme once; concurrent callers share the same in-flight request. */
  loadAndApply(force = false): Promise<void> {
    if (!force && this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.fetchAndApply().finally(() => {
      this.loadPromise = null;
    });

    return this.loadPromise;
  }

  private async fetchAndApply(): Promise<void> {
    try {
      const theme = await firstValueFrom(this.adminThemeService.getActiveTheme());
      this.applyTheme(theme);
    } catch (error) {
      console.warn("Admin theme unavailable; using default SCSS theme.", error);
    }
  }

  applyTheme(theme: AdminTheme | null | undefined): void {
    if (!theme) {
      return;
    }
    if (theme.tokens) {
      this.applyTokens(theme.tokens);
    }
    const resolved = resolveBrandingLogoUrl(theme.logoUrl);
    setBrandingLogoSrc(resolved);
    resetBrandingLogoDataUriCache();
    writeCachedAdminThemeLogo(resolved);
    updateFavicon(resolved);
    this.logoUrlSubject.next(resolved);
  }

  applyTokens(tokens: Record<string, string>): void {
    applyAdminThemeTokens(tokens);
    writeCachedAdminThemeTokens(tokens);
  }
}
